// Loader.js
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import * as THREE from "three";
import debug from "debug";

const log = debug("app:loader");

/**
 * Loader class for handling the loading of GLB models using GLTFLoader.
 * @category Experience
 * @subcategory World
 */
export default class Loader {
  /**
   * Constructor initializes the GLTFLoader instance.
   */
  constructor() {
    this.loader = new GLTFLoader(); // Instantiate the GLTFLoader
  }

  cleanup() {
    if (this.loader) {
        this.loader = null;
    }

    // Remove any references to loaded models to allow garbage collection
    this.loadedModels = null;
}

  // Method to load a GLB model and return a promise that resolves with the model
  /**
   * Loads a GLB model and returns a promise that resolves with the loaded model.
   * 
   * @param {string} glbPath - The URL or path to the GLB file.
   * @param {Object} environmentMap - Environment map texture for reflections.
   * @param {string} layer - Layer category to determine material properties.
   * @param {number} [scale=0.3] - Scale factor applied to the model.
   * @param {THREE.Vector3} [position=new THREE.Vector3(0, 0, 0)] - Position of the model in the scene.
   * @param {THREE.Vector3} [rotation=new THREE.Vector3(-Math.PI / 2, 0, 0)] - Rotation of the model.
   * @returns {Promise<THREE.Group>} - A Promise that resolves with the loaded model group.
   */
  async loadModel(glbPath, environmentMap, layer,scale = 0.3 ,position = new THREE.Vector3(0, 0, 0), rotation = new THREE.Vector3(-Math.PI / 2, 0, 0) ) {
    log(`Loading GLB from: ${glbPath}`);

    return new Promise((resolve, reject) => {
      this.loader.load(
        glbPath,
        (gltf) => {
          const model = gltf.scene;
          model.name = "XMO";

          // Create a group to hold the model and its bounding box
          const xmoGroup = new THREE.Group();
          xmoGroup.name = "XMO";
          xmoGroup.isGroup = true;
          const layery=layer
          // Set scale, position, and rotation
          xmoGroup.scale.set(scale, scale, scale);
          xmoGroup.position.copy(position);
          xmoGroup.rotation.set(rotation.x, rotation.y, rotation.z);

          // Apply environment map and other material settings
          model.traverse((child) => {
            if (child instanceof THREE.Mesh && child.material) {
              child.castShadow = true;
              child.receiveShadow = true;
              child.material.envMap = environmentMap.texture;
              child.material.envMapIntensity = environmentMap.intensity || 1;

              // Apply additional material settings based on the layer
              if (layery === "20" || layery === "33") {
                child.material.color.set("#e8e8e8"); // Light gray color for specific layers
                child.material.roughness = 0;        // Make the material less rough
                child.material.metalness = 1;        // No metalness
              } else {
                // Default material settings for other layers
                // child.material.color.set("#f0f0f0"); // Default color
                child.material.roughness = 0.5;      // Default roughness
                child.material.metalness = 0.1;      // Default metalness
              }

              // Ensure transparency properties are set correctly
              // Preserve existing opacity if set, otherwise default to 1.0
              const currentOpacity = child.material.opacity !== undefined ? child.material.opacity : 1.0;
              const shouldBeTransparent = currentOpacity < 1.0;
              
              child.material.opacity = currentOpacity;
              child.material.transparent = shouldBeTransparent;

              // Ensure the material updates
              child.material.needsUpdate = true;
            }
          });

          // Calculate bounding box of the model to size the transparent box
          const box = new THREE.Box3().setFromObject(model);
          const size = new THREE.Vector3();
          box.getSize(size);

          // Create a transparent bounding box
          /**
           * Creates a transparent bounding box for the loaded model.
           */
          const boundingBoxGeometry = new THREE.BoxGeometry(size.x, size.y, size.z);
          const boundingBoxMaterial = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0, // Fully transparent
            depthWrite: false, // Prevent writing to depth buffer
          });
          const boundingBox = new THREE.Mesh(boundingBoxGeometry, boundingBoxMaterial);
          boundingBox.name = "bBox";
          boundingBox.layers.set(20); // Exclude from Sobel edge detection (models-only depth pass)
          boundingBox.position.copy(box.getCenter(new THREE.Vector3()));

          // Add the model and bounding box to the group
          xmoGroup.add(model);
          xmoGroup.add(boundingBox);

          resolve(xmoGroup);
        },
        undefined, // onProgress callback, optional
        (error) => {
          console.error("Error loading GLB model:", error);
          reject(error);
        }
      );
    });
  }
}
